<template>
    <DsDropdown
        ref="campaignBuilderDropdown"
        icons
        dense
        is-open
    >
        <template v-if="contextMenuData && contextMenuOptions.length" #menu>
            <ul class="dropdown-menu-list campaign-context-menu">
                <li v-if="showCurrentFilterGoalsWarning" class="dropdown-menu-item tooltip-warning">
                    {{ $t('funnel.campaign.reporting.contextMenu.goalsFilterCurrentWarning') }}
                </li>
                <template v-else>
                    <li
                        v-for="contextMenuOption in contextMenuOptions"
                        :key="contextMenuOption.event"
                        :data-qa="contextMenuOption.dataQa"
                        class="dropdown-menu-item campaign-context-menu-item"
                        :class="contextMenuOption.classes"
                        @click="onClickEvent(contextMenuOption.event)"
                    >
                        <DsIcon v-if="contextMenuOption.icon" :name="contextMenuOption.icon" />
                        <img v-if="contextMenuOption.iconSrc" class="iconSrc" :src="contextMenuOption.iconSrc" />
                        {{ $t(contextMenuOption.label) }}{{ contextMenuOption.subtext || '' }}
                    </li>
                    <template v-if="isSequence && !showReportingView && selectedCells.length < 2">
                        <hr />
                        <li
                            class="dropdown-menu-item toggle-line-item"
                            @click="onClickToggleSequenceReady"
                        >
                            <DsToggleSwitch
                                data-qa="draft-toggle-switch"
                                :value="cell.value.ready === '1'"
                                :label-off="$t('funnel.edit.contextMenu.vertex.markAsDraft')"
                                :label-on="$t('funnel.edit.contextMenu.vertex.markAsReady')"
                                class="sequence-toggle"
                            />
                        </li>
                    </template>
                </template>
            </ul>
        </template>
    </DsDropdown>
</template>

<script>
import {
    PRESET_FILTERS,
    GOAL_EMAIL_CONFIRM,
    EDGE_TYPE,
    FLOW_TYPE,
    MAIN_CANVAS_ID,
    PROCESS_BARD_EMAIL,
    SEQUENCE_START,
    NOTES_TYPE,
    DECISION_TYPE,
    GRID_SIZE,
    TIMERS,
    PROCESS_APPLY_REMOVE_TAG,
} from '../utils/editor.constants';
import {
    isCellLocked,
    getCellById,
} from '../utils/cell/automation-cell-utils';
import { containsIncompleteDecisionDiamond } from '../decisionDiamonds/decision-utils';

const OPTIONS = {
    EDIT: {
        icon: 'edit',
        label: 'funnel.edit.contextMenu.vertex.viewEdit',
        dataQa: 'click-view-and-edit',
        event: 'click-view-and-edit',
    },
    SETTINGS: {
        icon: 'settings',
        label: 'funnel.edit.contextMenu.vertex.settings',
        dataQa: 'click-settings',
        event: 'click-settings',
    },
    DUPLICATE: {
        icon: 'copy',
        label: 'funnel.edit.contextMenu.vertex.duplicate',
        dataQa: 'click-duplicate',
        event: 'click-duplicate',
    },
    DUPLICATE_ALL: {
        icon: 'copy',
        label: 'funnel.edit.contextMenu.vertex.duplicateAll',
        dataQa: 'click-duplicate-all',
        event: 'click-duplicate-all',
    },
    EDIT_NOTE: {
        icon: 'edit',
        label: 'funnel.edit.contextMenu.vertex.editNote',
        dataQa: 'click-edit-note',
        event: 'click-edit-note',
    },
    RENAME: {
        icon: 'pencil',
        label: 'funnel.edit.contextMenu.vertex.rename',
        dataQa: 'click-rename',
        event: 'click-rename',
    },
    DELETE: {
        icon: 'trash-2',
        classes: 'red-icon',
        label: 'funnel.edit.contextMenu.vertex.delete',
        dataQa: 'delete',
        event: 'click-delete',
    },
    DELETE_ALL: {
        icon: 'trash-2',
        classes: 'red-icon',
        label: 'funnel.edit.contextMenu.vertex.deleteAll',
        dataQa: 'delete-all',
        event: 'click-delete-all',
    },
    DELETE_CONNECTION: {
        icon: 'trash-2',
        classes: 'red-icon',
        label: 'funnel.edit.contextMenu.vertex.deleteConnection',
        dataQa: 'delete',
        event: 'click-delete',
    },
    ACTIVE_CONTACTS: {
        iconSrc: '/resources/funnel/images/svg/contact-users.svg',
        label: 'funnel.campaign.reporting.contextMenu.activeContacts',
        dataQa: 'click-active-stats',
        event: 'click-active-stats',
    },
    QUEUED_CONTACTS: {
        icon: 'clock',
        label: 'funnel.campaign.reporting.contextMenu.queuedContacts',
        dataQa: 'click-queue-stats',
        event: 'click-queue-stats',
    },
    SEQUENCE_METRICS: {
        icon: 'campaign',
        label: 'funnel.campaign.reporting.contextMenu.sequenceMetrics',
        dataQa: 'click-sequence-metrics',
        event: 'click-view-and-edit',
    },
    NEW_EMAIL_EXPERIENCE: {
        icon: 'replace',
        label: 'funnel.context.menu.new.email',
        classes: 'trailing-icon-new',
        dataQa: 'click-new-email-experience',
        event: 'click-new-email-experience',
    },
};

export default {
    name: 'KonvaCampaignContextMenu',
    props: {
        preset: {
            type: String,
            default: '',
        },
        showReportingView: {
            type: Boolean,
            default: false,
        },
        contextMenuData: {
            type: Object,
            default: null,
        },
        flagsInfo: {
            type: Object,
            required: true,
        },
    },
    emits: [
        'click-view-and-edit',
        'click-settings',
        'click-duplicate',
        'click-duplicate-all',
        'click-edit-note',
        'click-rename',
        'click-delete',
        'click-delete-all',
        'click-delete-connection',
        'click-active-stats',
        'click-queue-stats',
        'click-sequence-metrics',
        'click-new-email-experience',
        'click-sequence-toggle-ready',
        'close',
    ],

    computed: {
        isSequence() {
            return this.cell.type === FLOW_TYPE;
        },

        isTimer() {
            return Object.values(TIMERS).includes(this.cell.type);
        },

        cellLocked() {
            return isCellLocked(this.cell, this.cells);
        },

        contextMenuOptions() {
            if (this.showCurrentFilterGoalsWarning) return [];

            if (this.showReportingView) return this.getReportingOptions();
            if (this.selectedCells.length > 1) return this.getMultiselectOptions();
            if (this.cell.type === SEQUENCE_START) return this.getStartOptions();
            if (this.cell.type === GOAL_EMAIL_CONFIRM) return this.getEmailConfirmOptions();
            if (this.cell.type === EDGE_TYPE) return this.getEdgeOptions();
            if (this.cell.type === NOTES_TYPE) return this.getNoteOptions();

            return this.getStandardEditorOptions();
        },

        isDecisionDiamondTarget() {
            if (!this.cells) return false;
            const targetCell = getCellById(this.cell.meta.target, this.cells);

            return targetCell?.type === DECISION_TYPE;
        },

        isDecisionDiamondCell() {
            return this.cell.type === DECISION_TYPE;
        },

        showCurrentFilterGoalsWarning() {
            return this.preset === PRESET_FILTERS.CURRENT
                && this.showReportingView
                && this.cell.meta.parent === MAIN_CANVAS_ID
                && this.cell.type !== FLOW_TYPE;
        },

        reportData() {
            return this.cell?.cellReportingData || {};
        },

        selectedCells() {
            return this.contextMenuData?.selectedCells;
        },

        cell() {
            return this.contextMenuData?.cell;
        },

        cells() {
            return this.contextMenuData?.cells;
        },

        isBardBuilderDisabled() {
            return this.flagsInfo?.bardDisabled ?? false;
        },
    },
    watch: {
        'contextMenuData.event.evt': {
            deep: true,
            handler(contextMenuEvent) {
                if (!contextMenuEvent) {
                    this.onCloseContextMenu();
                } else {
                    const element = this.$refs.campaignBuilderDropdown.$el;

                    element.style.opacity = 1;
                    element.style.left = contextMenuEvent.clientX;
                    element.style.top = contextMenuEvent.clientY - GRID_SIZE * 1.5;
                }
            },
        },
    },
    updated() {
        const dropdownElement = this.$refs.campaignBuilderDropdown.$el;
        const menuElement = dropdownElement.querySelector('.campaign-context-menu');

        if (menuElement) {
            const { height: clientHeight, width: clientWidth } = dropdownElement.parentElement.getBoundingClientRect();
            const {
                x,
                y,
                width,
                height,
            } = menuElement.getBoundingClientRect();

            if ((x + width) > clientWidth) {
                dropdownElement.style.left = clientWidth - (width + 5);
            }

            if ((y + height) > clientHeight) {
                dropdownElement.style.top = clientHeight - (height + 5);
            }
        }
    },
    methods: {
        getStartOptions() {
            if (this.cellLocked) return [];

            return [OPTIONS.DELETE];
        },

        getMultiselectOptions() {
            const hasLockedCells = this.selectedCells.some((cell) => isCellLocked(cell, this.cells));

            if (hasLockedCells) return [];

            const selectedCellIds = this.selectedCells.map((cell) => cell.id);

            const hasUnconnectedEdge = this.selectedCells.some((cell) => {
                if (cell.type !== EDGE_TYPE) return false;
                if (selectedCellIds.includes(cell.meta.source) && selectedCellIds.includes(cell.meta.target)) return false;

                return true;
            });

            if (hasUnconnectedEdge || containsIncompleteDecisionDiamond(this.selectedCells)) {
                return [OPTIONS.DELETE_ALL];
            }

            return [OPTIONS.DUPLICATE_ALL, OPTIONS.DELETE_ALL];
        },

        getEmailConfirmOptions() {
            return [OPTIONS.SETTINGS, OPTIONS.RENAME, OPTIONS.DELETE];
        },

        getEdgeOptions() {
            if (this.isDecisionDiamondTarget || this.showReportingView || this.cellLocked) return [];

            return [OPTIONS.DELETE_CONNECTION];
        },

        getNoteOptions() {
            if (this.cellLocked) return [];

            return [OPTIONS.EDIT_NOTE, OPTIONS.DUPLICATE, OPTIONS.DELETE];
        },

        getStandardEditorOptions() {
            const editorOptions = [];

            if (this.cellLocked) {
                return [OPTIONS.EDIT]; // TODO: Confirm this behavior
            }

            if (this.cell.type === PROCESS_BARD_EMAIL) {
                editorOptions.push(OPTIONS.NEW_EMAIL_EXPERIENCE);
            }

            if (!(this.isBardBuilderDisabled && this.cell.type === PROCESS_BARD_EMAIL)) {
                editorOptions.push(OPTIONS.EDIT);
            }

            if (this.cell.meta?.parent === MAIN_CANVAS_ID && !this.isDecisionDiamondCell) {
                editorOptions.push(OPTIONS.SETTINGS);
            }

            if (this.cell.type !== DECISION_TYPE) {
                editorOptions.push(OPTIONS.DUPLICATE);
            }

            if (this.cell.type !== PROCESS_APPLY_REMOVE_TAG) {
                editorOptions.push(OPTIONS.RENAME);
            }

            editorOptions.push(OPTIONS.DELETE);

            return editorOptions;
        },

        getReportingOptions() {
            const reportingOptions = [];

            if (this.preset !== PRESET_FILTERS.CURRENT) {
                reportingOptions.push({
                    iconSrc: '/resources/funnel/images/svg/contact-users.svg',
                    label: 'funnel.campaign.reporting.contextMenu.processedContacts',
                    dataQa: 'click-active-stats',
                    event: 'click-active-stats',
                    subtext: ` (${this.reportData.funnelStats || '0'})`,
                });
            }

            if (this.isSequence) {
                if (this.preset === PRESET_FILTERS.CURRENT) {
                    reportingOptions.push({
                        iconSrc: '/resources/funnel/images/svg/contact-users.svg',
                        label: 'funnel.campaign.reporting.contextMenu.activeContacts',
                        dataQa: 'click-active-stats',
                        event: 'click-active-stats',
                        subtext: ` (${this.reportData.activeStatistics || '0'})`,
                    });

                    reportingOptions.push({
                        icon: 'clock',
                        label: 'funnel.campaign.reporting.contextMenu.queuedContacts',
                        dataQa: 'click-queue-stats',
                        event: 'click-queue-stats',
                        subtext: ` (${this.reportData.activeQueueStatistics || '0'})`,
                        sequenceCellOnly: true,
                    });
                }

                reportingOptions.push({
                    icon: 'campaign',
                    label: 'funnel.campaign.reporting.contextMenu.sequenceMetrics',
                    dataQa: 'click-sequence-metrics',
                    event: 'click-view-and-edit',
                    sequenceCellOnly: true,
                });
            }

            if (this.isTimer && this.preset === PRESET_FILTERS.CURRENT) {
                reportingOptions.push({
                    iconSrc: '/resources/funnel/images/svg/contact-users.svg',
                    label: 'funnel.campaign.reporting.contextMenu.waitingContacts',
                    dataQa: 'click-waiting-stats',
                    event: 'click-waiting-stats',
                    subtext: ` (${this.reportData.waitingQueueStatistics || '0'})`,
                });
            }

            return reportingOptions;
        },

        onClickToggleSequenceReady() {
            this.onClickEvent('click-sequence-toggle-ready', { sequenceCell: this.cell });
        },

        onClickEvent(eventName) {
            this.$emit(eventName, this.cell);
            this.onCloseContextMenu();
        },

        onCloseContextMenu() {
            this.$emit('close');
            this.$refs.campaignBuilderDropdown.$el.style.opacity = 0;
        },
    },
};
</script>

<style scoped lang="scss">
hr {
    margin: 0;
    border-color: $color-gray-200;
}
.dropdown::v-deep .dropdown-menu {
    max-height: unset;
    min-width: unset;

    .campaign-context-menu .campaign-context-menu-item {
        display: grid;
        grid-template-columns: 2rem auto;
    }
}

.dropdown-menu.icons .iconSrc {
    margin-right: 1rem;
}

.toggle-line-item .sequence-toggle {
    color: $color-ink;
    font-size: $font-size-sm;
    font-weight: $font-weight-regular;
}
.red-icon {
    color: $color-red;
}
.red-icon svg {
    fill: $color-red;
}
.tooltip-warning {
    pointer-events: none;
    max-width: 220px;
    line-height: $line-height-lg;
}
ul.campaign-context-menu li.trailing-icon-new {
    width: 200px;
    background-image: url("/resources/funnel/images/svg/new.svg");
    background-repeat: no-repeat;
    background-position: px-to-rem(128) px-to-rem(26);
}

ul.campaign-context-menu li.trailing-icon-new svg{
    margin-top: -$spacing-250;
}
</style>
